import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMail, FiX, FiRefreshCw, FiChevronRight, FiLogIn, FiLoader, FiEdit3, FiFileText, FiInbox, FiSend, FiAlertCircle, FiChevronLeft, FiChevronsLeft, FiChevronsRight, FiLogOut } from 'react-icons/fi';
import { getInbox, listDrafts, listSent, listSpam, disconnectGmail, type EmailThread, type EmailDraft } from '../api/emailApi';
import { useEmailStore } from '../stores/emailStore';
import EmailReader from './EmailReader';
import EmailCompose from './EmailCompose';
import { getGoogleAuthUrl, getGoogleGmailAuthUrl } from '../api/googleApi';
import { getGoogleStatus as getGoogleStatusFromSettings, disconnectGoogle } from '../api/settingsApi';
import { getGmailStatus } from '../api/emailApi';
import { useClient } from '../contexts/ClientContext';
import { toast } from 'react-toastify';
import { useStorageReadOnly } from '../hooks/useStorageReadOnly';

interface EmailInboxPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function EmailInboxPopup({ isOpen, onClose }: EmailInboxPopupProps) {
  const { client } = useClient();
  const [threads, setThreads] = useState<EmailThread[]>([]);
  const [drafts, setDrafts] = useState<EmailDraft[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const [checkingConnection, setCheckingConnection] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectedEmail, setConnectedEmail] = useState<string | null>(null);
  const [needsReauth, setNeedsReauth] = useState(false);
  const [reconnectReason, setReconnectReason] = useState<'NOT_CONNECTED' | 'NEEDS_RECONNECT' | 'INSUFFICIENT_SCOPES' | 'SCOPES_UNKNOWN' | 'OK'>('NOT_CONNECTED');
  const [missingScopes, setMissingScopes] = useState<string[]>([]);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const hasCheckedConnection = useRef(false);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [view, setView] = useState<'inbox' | 'sent' | 'spam' | 'drafts'>('inbox');
  const [showCompose, setShowCompose] = useState(false);
  const [composeInitialData, setComposeInitialData] = useState<any>(null);
  
  // Filter state for read/unread
  const [readFilter, setReadFilter] = useState<'all' | 'unread' | 'read'>('all');
  
  // Pagination state
  const [pageSize, setPageSize] = useState<number>(5);
  const [pageTokens, setPageTokens] = useState<string[]>([]); // History of page tokens for back navigation
  const [currentPageToken, setCurrentPageToken] = useState<string | null>(null);
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [allThreads, setAllThreads] = useState<EmailThread[]>([]); // For lazy loading - accumulate all threads
  
  const { refreshUnreadCount } = useEmailStore();
  const popupRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { isReadOnly } = useStorageReadOnly();

  // Get company from URL or token
  const company = useMemo(() => {
    try {
      const pathname = window.location.pathname;
      const segments = pathname.split('/').filter(Boolean);
      
      // Check if superadmin route
      if (segments.includes('superadmin')) {
        return 'superadmin';
      }
      
      const reserved = ['admin', 'manager', 'agent', 'app', 'superadmin'];
      if (segments.length > 0 && !reserved.includes(segments[0])) {
        return segments[0];
      }
      // Fallback to token
      const token = localStorage.getItem('token') || '';
      const payload = JSON.parse(atob(token.split('.')[1] || ''));
      return payload.companyNumber || payload.company || '';
    } catch {
      return '';
    }
  }, []);

  // For superadmin, use 'superadmin' as adminId, otherwise use client._id
  const adminId = company === 'superadmin' ? 'superadmin' : (client?._id || '');

  // Filter threads based on read/unread filter
  const filteredThreads = useMemo(() => {
    if (readFilter === 'all') return threads;
    if (readFilter === 'unread') return threads.filter(t => t.unreadCount > 0);
    if (readFilter === 'read') return threads.filter(t => t.unreadCount === 0);
    return threads;
  }, [threads, readFilter]);

  // Load inbox function with pagination
  const loadInbox = async (reset = false, loadMore = false) => {
    // Prevent duplicate calls if already loading (unless resetting)
    if (loading && !reset && !loadMore) {
      return;
    }
    
    if (reset) {
      setAllThreads([]);
      setThreads([]);
      setPageTokens([]);
      setCurrentPageToken(null);
      setNextPageToken(null);
    }
    
    setLoading(true);
    try {
      // Use nextPageToken for lazy loading, currentPageToken for pagination
      const tokenToUse = loadMore ? nextPageToken : (reset ? null : currentPageToken);
      
      const result = await getInbox({
        pageSize: pageSize,
        pageToken: tokenToUse || undefined,
      });
      
      
      if (result?.threads && Array.isArray(result.threads)) {
        if (loadMore) {
          // Append to existing threads for lazy loading
          setAllThreads(prev => {
            const newThreads = [...prev, ...result.threads];
            setThreads(newThreads);
            return newThreads;
          });
        } else {
          // Replace threads for pagination
          setAllThreads(result.threads);
          setThreads(result.threads);
        }
        setNextPageToken(result.pagination?.nextPageToken || null);
        setHasMore(result.pagination?.hasMore || false);
        // Refresh unread count after loading
        refreshUnreadCount(client?.endDate, client?.role);
      } else {
        console.warn('[EmailInboxPopup] No threads in result:', result);
        // Still set empty arrays to show empty state
        setAllThreads([]);
        setThreads([]);
        setNextPageToken(null);
        setHasMore(false);
      }
    } catch (err: any) {
      // Handle standardized error codes
      const errorCode = err?.code || err?.response?.data?.code;
      
      if (errorCode === 'NOT_CONNECTED') {
        setNeedsReauth(true);
        setIsConnected(false);
        setAllThreads([]);
        setThreads([]);
        setNextPageToken(null);
        setHasMore(false);
        return;
      }
      
      if (errorCode === 'NEEDS_RECONNECT') {
        setNeedsReauth(true);
        setIsConnected(false);
        toast.error('חיבור Gmail פג תוקף. יש להתחבר מחדש.');
        setAllThreads([]);
        setThreads([]);
        setNextPageToken(null);
        setHasMore(false);
        return;
      }
      
      if (errorCode === 'INSUFFICIENT_SCOPES' || errorCode === 'GOOGLE_SCOPES_MISSING') {
        setNeedsReauth(true);
        setIsConnected(false);
        toast.error('צריך להתחבר מחדש ל-Gmail (הרשאות חסרות).');
        setAllThreads([]);
        setThreads([]);
        setNextPageToken(null);
        setHasMore(false);
        return;
      }
      
      // Only log non-500 errors (500 errors are handled gracefully in emailApi)
      if (err?.response?.status !== 500) {
        console.error('[EmailInboxPopup] Failed to load inbox:', err);
      }
      // Set empty state on error
      setAllThreads([]);
      setThreads([]);
      setNextPageToken(null);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  // Load drafts function with pagination
  const loadDrafts = async (reset = false) => {
    if (reset) {
      setDrafts([]);
    }
    setLoadingDrafts(true);
    try {
      const result = await listDrafts({
        pageSize: pageSize,
      });
      if (result?.drafts) {
        setDrafts(reset ? result.drafts : [...drafts, ...result.drafts]);
      }
    } catch (err: any) {
      // Handle scope/auth errors - stop retrying
      const isScopeError = err?.code === 'GOOGLE_SCOPES_MISSING' || 
                          err?.code === 'GOOGLE_REAUTH_REQUIRED' ||
                          err?.response?.data?.code === 'GOOGLE_SCOPES_MISSING' ||
                          err?.response?.data?.code === 'GOOGLE_REAUTH_REQUIRED' ||
                          (err?.response?.status === 401 && err?.response?.data?.needsReauth === true);
      
      if (isScopeError) {
        setNeedsReauth(true);
        setLoadingDrafts(false); // Stop loading immediately
        setDrafts([]);
        return; // Stop execution - don't retry
      }
      console.error('[EmailInboxPopup] Failed to load drafts:', err);
    } finally {
      setLoadingDrafts(false);
    }
  };

  // Load sent emails function with pagination
  const loadSent = async (reset = false, loadMore = false) => {
    if (reset) {
      setAllThreads([]);
      setThreads([]);
      setPageTokens([]);
      setCurrentPageToken(null);
      setNextPageToken(null);
    }
    setLoading(true);
    try {
      const tokenToUse = loadMore ? nextPageToken : (reset ? null : currentPageToken);
      
      const result = await listSent({
        pageSize: pageSize,
        pageToken: tokenToUse || undefined,
      });
      
      if (result?.threads) {
        if (loadMore) {
          setAllThreads(prev => {
            const newThreads = [...prev, ...result.threads];
            setThreads(newThreads);
            return newThreads;
          });
        } else {
          setAllThreads(result.threads);
          setThreads(result.threads);
        }
        setNextPageToken(result.pagination?.nextPageToken || null);
        setHasMore(result.pagination?.hasMore || false);
      }
    } catch (err: any) {
      // Handle scope/auth errors - stop retrying
      const isScopeError = err?.code === 'GOOGLE_SCOPES_MISSING' || 
                          err?.code === 'GOOGLE_REAUTH_REQUIRED' ||
                          err?.response?.data?.code === 'GOOGLE_SCOPES_MISSING' ||
                          err?.response?.data?.code === 'GOOGLE_REAUTH_REQUIRED' ||
                          (err?.response?.status === 401 && err?.response?.data?.needsReauth === true) ||
                          (err?.response?.status === 403 && err?.response?.data?.code === 'GOOGLE_SCOPES_MISSING');
      
      if (isScopeError) {
        setNeedsReauth(true);
        setLoading(false); // Stop loading immediately
        setAllThreads([]);
        setThreads([]);
        setNextPageToken(null);
        setHasMore(false);
        return; // Stop execution - don't retry
      }
      console.error('[EmailInboxPopup] Failed to load sent emails:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load spam emails function with pagination
  const loadSpam = async (reset = false, loadMore = false) => {
    
    if (reset) {
      setAllThreads([]);
      setThreads([]);
      setPageTokens([]);
      setCurrentPageToken(null);
      setNextPageToken(null);
    }
    setLoading(true);
    try {
      const tokenToUse = loadMore ? nextPageToken : (reset ? null : currentPageToken);
            
      const result = await listSpam({
        pageSize: pageSize,
        pageToken: tokenToUse || undefined,
      });
            
      if (result?.threads) {
        if (loadMore) {
          setAllThreads(prev => {
            const newThreads = [...prev, ...result.threads];
            setThreads(newThreads);
            return newThreads;
          });
        } else {
          setAllThreads(result.threads);
          setThreads(result.threads);
        }
        setNextPageToken(result.pagination?.nextPageToken || null);
        setHasMore(result.pagination?.hasMore || false);
      } else {
      }
    } catch (err: any) {
      // Handle scope/auth errors - stop retrying
      const isScopeError = err?.code === 'GOOGLE_SCOPES_MISSING' || 
                          err?.code === 'GOOGLE_REAUTH_REQUIRED' ||
                          err?.response?.data?.code === 'GOOGLE_SCOPES_MISSING' ||
                          err?.response?.data?.code === 'GOOGLE_REAUTH_REQUIRED' ||
                          (err?.response?.status === 401 && err?.response?.data?.needsReauth === true) ||
                          (err?.response?.status === 403 && err?.response?.data?.code === 'GOOGLE_SCOPES_MISSING');
      
      if (isScopeError) {
        setNeedsReauth(true);
        setLoading(false); // Stop loading immediately
        setAllThreads([]);
        setThreads([]);
        setNextPageToken(null);
        setHasMore(false);
        return; // Stop execution - don't retry
      }
      console.error('[EmailInboxPopup] Failed to load spam emails:', err);
      toast.error('שגיאה בטעינת ספאם: ' + ((err as Error)?.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  // Load data based on current view
  useEffect(() => {
    if (isOpen && isConnected && !needsReauth) {
      if (view === 'inbox') {
        loadInbox(true);
      } else if (view === 'sent') {
        loadSent(true);
      } else if (view === 'spam') {
        loadSpam(true);
      } else if (view === 'drafts') {
        loadDrafts(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isConnected, needsReauth, view, pageSize]);

  // Track if we're currently loading more to prevent duplicate requests
  const isLoadingMore = useRef(false);

  // Lazy loading - detect scroll near bottom
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || loading || !hasMore || isLoadingMore.current) return;

    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;

    // Load more when 200px from bottom
    if (scrollHeight - scrollTop - clientHeight < 200) {
      isLoadingMore.current = true;
      
      if (view === 'inbox') {
        loadInbox(false, true).finally(() => {
          isLoadingMore.current = false;
        });
      } else if (view === 'sent') {
        loadSent(false, true).finally(() => {
          isLoadingMore.current = false;
        });
      } else if (view === 'spam') {
        loadSpam(false, true).finally(() => {
          isLoadingMore.current = false;
        });
      }
    }
  }, [loading, hasMore, view, nextPageToken, pageSize]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || view === 'drafts') return;

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll, view]);

  // Pagination functions
  const goToNextPage = () => {
    if (!hasMore || !nextPageToken) return;
    setPageTokens(prev => [...prev, currentPageToken || '']);
    setCurrentPageToken(nextPageToken);
    setNextPageToken(null);
    if (view === 'inbox') {
      loadInbox(false, false);
    } else if (view === 'sent') {
      loadSent(false, false);
    } else if (view === 'spam') {
      loadSpam(false, false);
    }
  };

  const goToPrevPage = () => {
    if (pageTokens.length === 0) return;
    const prevToken = pageTokens[pageTokens.length - 1];
    setPageTokens(prev => prev.slice(0, -1));
    setCurrentPageToken(prevToken);
    setNextPageToken(null);
    if (view === 'inbox') {
      loadInbox(false, false);
    } else if (view === 'sent') {
      loadSent(false, false);
    } else if (view === 'spam') {
      loadSpam(false, false);
    }
  };

  const goToFirstPage = () => {
    setPageTokens([]);
    setCurrentPageToken(null);
    setNextPageToken(null);
    if (view === 'inbox') {
      loadInbox(true);
    } else if (view === 'sent') {
      loadSent(true);
    } else if (view === 'spam') {
      loadSpam(true);
    }
  };

  const handleCompose = (initialData?: any) => {
    setComposeInitialData(initialData || null);
    setShowCompose(true);
  };

  const handleComposeClose = () => {
    setShowCompose(false);
    setComposeInitialData(null);
    // Reload drafts if viewing drafts
    if (view === 'drafts') {
      loadDrafts();
    }
    // Reload inbox after sending
    if (view === 'inbox') {
      loadInbox();
    }
  };

  // Handle Google OAuth connection
  const handleConnectGoogle = () => {
    if (!company || !adminId) {
      toast.error('חסרים פרטים להתחברות');
      return;
    }

    const currentPath = window.location.pathname + window.location.search;
    const state = JSON.stringify({
      company,
      adminId,
      returnTo: currentPath,
    });
    
    // CRITICAL: Use Gmail incremental auth since this component is for email inbox
    // This requests Gmail scopes separately to avoid "unverified app" warning
    const authUrl = getGoogleGmailAuthUrl(company, state);
    window.location.href = authUrl;
  };

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
      }
    }
    
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Reset connection check when popup closes (so it rechecks on next open)
  useEffect(() => {
    if (!isOpen) {
      // Reset when popup closes so it will check again on next open
      hasCheckedConnection.current = false;
    }
  }, [isOpen]);

  // Check Google connection status when opened
  // Check Google connection status when popup opens (only once or if not connected)
  useEffect(() => {
    if (!isOpen || !company) return;
    
    // For superadmin, adminId should be 'superadmin', for others it should be the client._id
    const userId = company === 'superadmin' ? 'superadmin' : adminId;
    if (!userId) {
      console.warn('[EmailInboxPopup] No userId available, skipping connection check');
      setCheckingConnection(false);
      setIsConnected(false);
      return;
    }
    
    // Check if we're returning from OAuth redirect - reset connection check
    // This happens when user clicks "Reconnect to Gmail" and comes back
    // The OAuth callback redirects with ?google=connected parameter
    const urlParams = new URLSearchParams(window.location.search);
    const fromOAuth = urlParams.get('google') === 'connected' || 
                      urlParams.get('code') !== null ||
                      window.location.hash.includes('access_token');
    
    if (fromOAuth) {
      // Reset connection check to force fresh check after OAuth
      hasCheckedConnection.current = false;
      setNeedsReauth(false);
      // Clean up URL params (remove google=connected and code if present)
      if (urlParams.get('google') === 'connected' || urlParams.get('code') !== null) {
        urlParams.delete('google');
        urlParams.delete('code');
        const newSearch = urlParams.toString();
        const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + (window.location.hash || '');
        window.history.replaceState({}, '', newUrl);
      }
      // Small delay to ensure OAuth callback has finished saving tokens to DB
      // This ensures status check gets fresh data
      setTimeout(() => {
        hasCheckedConnection.current = false;
      }, 500);
    }
    
    // Only check connection if we haven't checked before or if we know we're not connected
    // If already connected, skip the check and just load the inbox
    if (hasCheckedConnection.current && isConnected && !fromOAuth) {
      // Already connected, just load inbox if needed (and not already loading)
      if (threads.length === 0 && view === 'inbox' && !loading) {
        loadInbox();
      }
      refreshUnreadCount(client?.endDate, client?.role);
      return;
    }
    
    const checkConnection = async () => {
      setCheckingConnection(true);
      try {
        // Use unified gmail/status endpoint (works for both regular admin and superadmin)
        const status = await getGmailStatus();
        
        // CRITICAL: Use status.needsReconnect as source of truth
        // If needsReconnect is false, user is connected (even if token was just refreshed)
        const isConnectedValue = status.connected && !status.needsReconnect;
        const needsReauthStatus = status.needsReconnect;
        
        setIsConnected(isConnectedValue);
        setNeedsReauth(needsReauthStatus);
        setReconnectReason(status.reason || 'NOT_CONNECTED');
        setMissingScopes(status.missingScopes || []);
        hasCheckedConnection.current = true;
        
        
        // If connected and doesn't need reauth, load inbox and refresh unread count
        if (isConnectedValue && !needsReauthStatus) {
          // Load inbox - errors are handled gracefully in loadInbox (only if not already loading)
          if (!loading) {
            loadInbox();
          }
          // Refresh unread count when email is connected
          refreshUnreadCount(client?.endDate, client?.role);
        } else {
          // If not connected or needs reauth, reset unread count
          refreshUnreadCount(client?.endDate, client?.role);
        }
      } catch (err) {
        console.error('[EmailInboxPopup] Failed to check Gmail status:', err);
        setIsConnected(false);
        setNeedsReauth(true);
        hasCheckedConnection.current = true; // Mark as checked even on error
      } finally {
        setCheckingConnection(false);
      }
    };

    checkConnection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, company, adminId]);

  // Handle Gmail disconnect
  const handleDisconnect = async () => {
    try {
      setDisconnecting(true);
      await disconnectGmail();
      toast.success('Gmail נותק בהצלחה');
      // Reset connection state
      setIsConnected(false);
      setNeedsReauth(true);
      hasCheckedConnection.current = false;
      setConnectedEmail(null);
      // Clear threads
      setThreads([]);
      setAllThreads([]);
      // Refresh unread count
      refreshUnreadCount(client?.endDate, client?.role);
      // Close confirmation modal
      setShowDisconnectConfirm(false);
    } catch (err: any) {
      console.error('[EmailInboxPopup] Failed to disconnect Gmail:', err);
      toast.error(err?.message || 'שגיאה בניתוק Gmail');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleThreadClick = (threadId: string) => {
    setSelectedThreadId(threadId);
  };

  const handleBackToList = () => {
    setSelectedThreadId(null);
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="email-inbox-popup"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-0 md:p-4"
            onClick={(e) => {
              // Close when clicking the backdrop
              if (e.target === e.currentTarget) {
                onClose();
              }
            }}
          >
        <motion.div
          ref={popupRef}
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-2xl md:rounded-2xl rounded-t-3xl shadow-2xl w-full max-w-4xl h-[95vh] md:h-[80vh] flex flex-col overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between p-3 md:p-4 border-b border-slate-200 gap-3 md:gap-0">
            <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
              <FiMail className="text-blue-600 text-lg md:text-xl flex-shrink-0" />
              <div className="flex flex-col min-w-0">
                <h2 className="text-base md:text-lg font-bold text-slate-900 truncate"></h2>
                {isConnected && (
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${connectedEmail ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
                    <span 
                      className={`text-xs truncate ${connectedEmail ? 'text-slate-600 font-medium' : 'text-slate-500'}`} 
                      title={connectedEmail || 'מחובר'}
                    >
                      {connectedEmail || 'מחובר'}
                    </span>
                  </div>
                )}
              </div>
              {isConnected && (
                <div className="flex items-center gap-1 md:gap-1 border-r border-slate-200 pr-2 md:pr-3 mr-2 md:mr-3 overflow-x-auto scrollbar-hide -mr-2 md:mr-3">
                  <button
                    onClick={() => setView('inbox')}
                    className={`px-2 md:px-3 py-1.5 md:py-1 rounded-lg transition-colors flex items-center gap-1 md:gap-2 whitespace-nowrap text-xs md:text-sm ${
                      view === 'inbox' 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    <FiInbox className="text-sm md:text-base" />
                    <span className="hidden sm:inline">נכנס</span>
                  </button>
                  <button
                    onClick={() => setView('sent')}
                    className={`px-2 md:px-3 py-1.5 md:py-1 rounded-lg transition-colors flex items-center gap-1 md:gap-2 whitespace-nowrap text-xs md:text-sm ${
                      view === 'sent' 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    <FiSend className="text-sm md:text-base" />
                    <span className="hidden sm:inline">נשלח</span>
                  </button>
                  <button
                    onClick={() => setView('spam')}
                    className={`px-2 md:px-3 py-1.5 md:py-1 rounded-lg transition-colors flex items-center gap-1 md:gap-2 whitespace-nowrap text-xs md:text-sm ${
                      view === 'spam' 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    <FiAlertCircle className="text-sm md:text-base" />
                    <span className="hidden sm:inline">ספאם</span>
                  </button>
                  <button
                    onClick={() => setView('drafts')}
                    className={`px-2 md:px-3 py-1.5 md:py-1 rounded-lg transition-colors flex items-center gap-1 md:gap-2 whitespace-nowrap text-xs md:text-sm ${
                      view === 'drafts' 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    <FiFileText className="text-sm md:text-base" />
                    <span className="hidden sm:inline">טיוטות</span>
                  </button>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {isConnected && (
                <>
                  <button
                    onClick={() => setShowDisconnectConfirm(true)}
                    className="px-3 md:px-4 py-2 rounded-lg transition-colors flex items-center gap-1 md:gap-2 text-sm md:text-base bg-red-100 text-red-700 hover:bg-red-200"
                    title="נתק Gmail"
                  >
                    <FiLogOut className="text-base md:text-lg" />
                    <span className="hidden sm:inline">נתק</span>
                  </button>
                  <button
                    onClick={() => {
                      if (isReadOnly) {
                        toast.error('האחסון מלא - לא ניתן לכתוב או לערוך מיילים. אנא פנה למנהל המערכת כדי לפנות מקום באחסון.');
                        return;
                      }
                      handleCompose();
                    }}
                    disabled={isReadOnly}
                    className={`px-3 md:px-4 py-2 rounded-lg transition-colors flex items-center gap-1 md:gap-2 text-sm md:text-base ${
                      isReadOnly
                        ? 'bg-slate-400 text-white cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                    title={isReadOnly ? 'האחסון מלא - לא ניתן לכתוב או לערוך מיילים. אנא פנה למנהל המערכת כדי לפנות מקום באחסון.' : 'כתוב מייל'}
                  >
                    <FiEdit3 className="text-base md:text-lg" />
                    <span className="hidden sm:inline">כתוב</span>
                  </button>
                  <button
                    onClick={() => {
                      if (view === 'inbox') {
                        loadInbox();
                      } else if (view === 'sent') {
                        loadSent();
                      } else if (view === 'spam') {
                        loadSpam();
                      } else if (view === 'drafts') {
                        loadDrafts();
                      }
                    }}
                    disabled={loading || loadingDrafts}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors touch-manipulation"
                    aria-label="רענן"
                  >
                    <FiRefreshCw className={`text-slate-600 text-base md:text-lg ${(loading || loadingDrafts) ? 'animate-spin' : ''}`} />
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        const userId = company === 'superadmin' ? 'superadmin' : adminId;
                        await disconnectGoogle(company, undefined, userId);
                        setIsConnected(false);
                        hasCheckedConnection.current = false; // Reset so it will check again next time
                        setThreads([]);
                        setDrafts([]);
                        toast.success('החיבור ל-Gmail נותק בהצלחה');
                      } catch (err: any) {
                        console.error('Disconnect Google failed:', err);
                        toast.error(err?.response?.data?.error || 'שגיאה בניתוק חיבור Gmail');
                      }
                    }}
                    className="p-2 hover:bg-red-50 rounded-lg transition-colors touch-manipulation"
                    aria-label="נתק Gmail"
                    title="נתק חיבור Gmail"
                  >
                    <FiLogOut className="text-red-600 text-base md:text-lg" />
                  </button>
                </>
              )}
              <button
                onClick={onClose}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors touch-manipulation"
                aria-label="סגור"
              >
                <FiX className="text-slate-600 text-base md:text-lg" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden flex">
            {checkingConnection ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <FiLoader className="text-4xl mx-auto mb-2 text-blue-600 animate-spin" />
                  <p className="text-slate-500">בודק חיבור...</p>
                </div>
              </div>
            ) : !isConnected || needsReauth ? (
              <div className="flex-1 flex items-center justify-center p-8">
                <div className="text-center max-w-md">
                  <div className="mb-6">
                    <FiMail className="text-6xl mx-auto mb-4 text-slate-400" />
                    <h3 className="text-xl font-bold text-slate-900 mb-2">
                      {reconnectReason === 'SCOPES_UNKNOWN' 
                        ? 'נדרש אימות הרשאות'
                        : reconnectReason === 'INSUFFICIENT_SCOPES'
                        ? 'הרשאות חסרות'
                        : needsReauth 
                        ? 'חיבור פג תוקף' 
                        : 'התחבר לחשבון Gmail שלך'}
                    </h3>
                    <p className="text-slate-600 text-sm">
                      {reconnectReason === 'SCOPES_UNKNOWN'
                        ? 'אנחנו צריכים שתחבר מחדש את Google כדי לאמת את ההרשאות שלך.'
                        : reconnectReason === 'INSUFFICIENT_SCOPES'
                          ? missingScopes.length > 0
                          ? `הרשאות חסרות: ${missingScopes.map(s => {
                              if (s.includes('gmail.send')) return 'שליחת מיילים';
                              // gmail.readonly is not verified yet, so don't show it
                              return s;
                            }).join(', ')}. יש להתחבר מחדש עם ההרשאות הנדרשות.`
                          : 'הרשאות Gmail חסרות. יש להתחבר מחדש עם ההרשאות הנדרשות.'
                        : needsReauth 
                        ? 'החיבור ל-Gmail פג תוקף או שהרשאות השתנו. יש להתחבר מחדש מהדף הגדרות.'
                        : 'כדי לצפות במיילים שלך, עליך להתחבר לחשבון Gmail שלך תחילה מהדף הגדרות.'
                      }
                    </p>
                  </div>
                  
                  <div className="space-y-4">
                    <button
                      onClick={() => {
                        if (needsReauth) {
                          // Direct reconnect - open OAuth URL directly
                          const state = JSON.stringify({
                            company,
                            adminId,
                            returnTo: window.location.pathname + window.location.search,
                          });
                          const authUrl = getGoogleGmailAuthUrl(company, state);
                          window.location.href = authUrl;
                        } else {
                          // Navigate to settings page with Google connection tab
                          const settingsPath = company === 'superadmin' 
                            ? '/superadmin/settings?tab=google'
                            : `/${company}/admin/settings?tab=google`;
                          window.location.href = settingsPath;
                        }
                      }}
                      className="
                        w-full inline-flex items-center justify-center gap-3
                        px-6 py-3 rounded-xl
                        bg-gradient-to-r from-blue-600 to-blue-700
                        text-white font-semibold
                        hover:from-blue-700 hover:to-blue-800
                        transition-all duration-200
                        shadow-lg hover:shadow-xl
                        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                      "
                    >
                      <FiLogIn className="text-xl" />
                      {needsReauth ? 'התחבר מחדש ל-Gmail' : 'פתח הגדרות'}
                    </button>
                    
                    <p className="text-xs text-slate-500">
                      {needsReauth
                        ? 'בדף ההגדרות תוכל להתחבר מחדש ל-Gmail עם ההרשאות הנדרשות'
                        : 'בדף ההגדרות תוכל להתחבר לחשבון Gmail שלך'
                      }
                    </p>
                  </div>
                </div>
              </div>
            ) : selectedThreadId ? (
              <EmailReader
                threadId={selectedThreadId}
                onBack={handleBackToList}
                onReply={(replyData) => handleCompose(replyData)}
                onMarkedAsSpam={() => {
                  // Reload current view after marking as spam
                  if (view === 'inbox') {
                    loadInbox();
                  } else if (view === 'spam') {
                    loadSpam();
                  }
                }}
                onDeleted={() => {
                  // Reload current view after deleting
                  if (view === 'inbox') {
                    loadInbox();
                  } else if (view === 'sent') {
                    loadSent();
                  } else if (view === 'spam') {
                    loadSpam();
                  }
                  setSelectedThreadId(null);
                }}
              />
            ) : view === 'sent' || view === 'spam' ? (
              <div className="flex-1 flex flex-col overflow-hidden">
              <div ref={scrollContainerRef} className="flex-1 overflow-y-auto p-2 md:p-4">
                {loading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <div className="relative mb-4">
                        {view === 'sent' ? (
                          <>
                            <FiSend className="text-5xl mx-auto text-blue-600 animate-pulse" />
                            <FiLoader className="absolute -top-1 -right-1 text-2xl text-blue-400 animate-spin" />
                          </>
                        ) : view === 'spam' ? (
                          <>
                            <FiAlertCircle className="text-5xl mx-auto text-orange-600 animate-pulse" />
                            <FiLoader className="absolute -top-1 -right-1 text-2xl text-orange-400 animate-spin" />
                          </>
                        ) : (
                          <>
                            <FiMail className="text-5xl mx-auto text-blue-600 animate-pulse" />
                            <FiLoader className="absolute -top-1 -right-1 text-2xl text-blue-400 animate-spin" />
                          </>
                        )}
                      </div>
                      <div className="flex items-center justify-center gap-1 text-slate-600 font-medium">
                        {view === 'sent' ? (
                          <>
                            <span className="animate-bounce" style={{ animationDelay: '0ms' }}>בודק</span>
                            <span className="animate-bounce" style={{ animationDelay: '150ms' }}>מיילים</span>
                            <span className="animate-bounce" style={{ animationDelay: '300ms' }}>נשלחים</span>
                          </>
                        ) : view === 'spam' ? (
                          <>
                            <span className="animate-bounce" style={{ animationDelay: '0ms' }}>בודק</span>
                            <span className="animate-bounce" style={{ animationDelay: '150ms' }}>ספאם</span>
                          </>
                        ) : (
                          <>
                            <span className="animate-bounce" style={{ animationDelay: '0ms' }}>מביא</span>
                            <span className="animate-bounce" style={{ animationDelay: '150ms' }}>מיילים</span>
                            <span className="animate-bounce" style={{ animationDelay: '300ms' }}>חדשים</span>
                          </>
                        )}
                        <span className="flex gap-0.5">
                          <span className="animate-bounce" style={{ animationDelay: '450ms' }}>.</span>
                          <span className="animate-bounce" style={{ animationDelay: '600ms' }}>.</span>
                          <span className="animate-bounce" style={{ animationDelay: '750ms' }}>.</span>
                        </span>
                      </div>
                    </div>
                  </div>
                ) : threads.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center text-slate-500">
                      {view === 'sent' ? (
                        <>
                          <FiSend className="text-4xl mx-auto mb-2 opacity-50" />
                          <p>אין מיילים נשלחים</p>
                        </>
                      ) : (
                        <>
                          <FiAlertCircle className="text-4xl mx-auto mb-2 opacity-50" />
                          <p>אין הודעות ספאם</p>
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      {threads.map((thread, index) => (
                        <motion.div
                          key={`thread-${thread.id || index}-${index}`}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          onClick={() => handleThreadClick(thread.id)}
                          className={`
                            p-3 md:p-4 rounded-lg border border-slate-200
                            hover:bg-slate-50 active:bg-slate-100 cursor-pointer
                            transition-colors touch-manipulation
                            ${view === 'spam' ? 'bg-red-50 border-red-200' : ''}
                          `}
                        >
                          <div className="flex items-start justify-between gap-2 md:gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1 md:gap-2 mb-1 flex-wrap">
                                <span className="font-semibold text-slate-900 truncate text-sm md:text-base">
                                  {thread.subject || '(ללא נושא)'}
                                </span>
                                {view === 'spam' && (
                                  <span className="bg-red-100 text-red-700 text-[10px] md:text-xs px-1 md:px-1.5 lg:px-2 py-0.5 rounded-full flex-shrink-0 leading-tight">
                                    ספאם
                                  </span>
                                )}
                              </div>
                              <p className="text-xs md:text-sm text-slate-600 truncate">
                                {view === 'sent' ? `אל: ${thread.from}` : thread.from}
                              </p>
                              <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                                {thread.snippet}
                              </p>
                              <p className="text-xs text-slate-400 mt-1">
                                {new Date(thread.lastMessageDate).toLocaleDateString('he-IL', {
                                  day: 'numeric',
                                  month: 'short',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </p>
                            </div>
                            <FiChevronRight className="text-slate-400 flex-shrink-0 text-base md:text-lg mt-0.5" />
                          </div>
                        </motion.div>
                      ))}
                    </div>
                    
                    {/* Loading indicator at bottom when loading more */}
                    {loading && threads.length > 0 && (
                      <div className="flex items-center justify-center py-4 mt-2">
                        <FiLoader className="text-2xl text-blue-600 animate-spin mr-2" />
                        <span className="text-sm text-slate-500">טוען עוד מיילים...</span>
                      </div>
                    )}
                  </>
                )}
              </div>
              
              {/* Pagination Controls */}
              {(view === 'sent' || view === 'spam') && threads.length > 0 && (
                <div className="border-t border-slate-200 p-2 md:p-3 bg-slate-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs md:text-sm text-slate-600 whitespace-nowrap">מספר פריטים לדף:</span>
                    <select
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        goToFirstPage();
                      }}
                      className="px-2 py-1.5 md:py-1 border border-slate-300 rounded-lg text-xs md:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 touch-manipulation"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-1 md:gap-2 flex-wrap justify-center sm:justify-end">
                    <button
                      onClick={goToFirstPage}
                      disabled={pageTokens.length === 0}
                      className="px-2 md:px-3 py-1.5 md:py-1 text-xs md:text-sm border border-slate-300 rounded-lg hover:bg-slate-100 active:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 touch-manipulation"
                    >
                      <FiChevronsRight className="text-sm md:text-base" />
                      <span className="hidden sm:inline">ראשון</span>
                    </button>
                    <button
                      onClick={goToPrevPage}
                      disabled={pageTokens.length === 0}
                      className="px-2 md:px-3 py-1.5 md:py-1 text-xs md:text-sm border border-slate-300 rounded-lg hover:bg-slate-100 active:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 touch-manipulation"
                    >
                      <FiChevronRight className="text-sm md:text-base" />
                      <span className="hidden sm:inline">הקודם</span>
                    </button>
                    <span className="text-xs md:text-sm text-slate-600 px-2 md:px-3 whitespace-nowrap">
                      עמוד {pageTokens.length + 1}
                    </span>
                    <button
                      onClick={goToNextPage}
                      disabled={!hasMore || !nextPageToken}
                      className="px-2 md:px-3 py-1.5 md:py-1 text-xs md:text-sm border border-slate-300 rounded-lg hover:bg-slate-100 active:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 touch-manipulation"
                    >
                      <span className="hidden sm:inline">הבא</span>
                      <FiChevronLeft className="text-sm md:text-base" />
                    </button>
                    {hasMore && (
                      <span className="text-xs text-slate-500 whitespace-nowrap">
                        ({threads.length} פריטים)
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
            ) : view === 'drafts' ? (
              <div className="flex-1 overflow-y-auto p-2 md:p-4">
                {loadingDrafts ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <FiLoader className="text-4xl mx-auto mb-2 text-blue-600 animate-spin" />
                      <div className="text-slate-500">טוען טיוטות...</div>
                    </div>
                  </div>
                ) : drafts.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center text-slate-500">
                      <FiFileText className="text-4xl mx-auto mb-2 opacity-50" />
                      <p>אין טיוטות</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {drafts.map((draft, index) => (
                      <motion.div
                        key={`draft-${draft.id || draft.draftId || index}-${index}`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        onClick={() => handleCompose({
                          draftId: draft.draftId,
                          to: draft.to,
                          cc: draft.cc,
                          bcc: draft.bcc,
                          subject: draft.subject,
                          bodyHtml: draft.bodyHtml,
                          bodyText: draft.bodyText,
                          threadId: draft.threadId,
                        })}
                        className="
                          p-3 md:p-4 rounded-lg border border-slate-200
                          hover:bg-slate-50 active:bg-slate-100 cursor-pointer
                          transition-colors touch-manipulation
                        "
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1 md:gap-2 mb-1 flex-wrap">
                              <span className="font-semibold text-slate-900 truncate text-sm md:text-base">
                                {draft.subject || '(ללא נושא)'}
                              </span>
                              <span className="bg-yellow-100 text-yellow-700 text-[10px] md:text-xs px-1 md:px-1.5 lg:px-2 py-0.5 rounded-full flex-shrink-0 leading-tight">
                                טיוטה
                              </span>
                            </div>
                            <p className="text-sm text-slate-600 truncate">
                              אל: {draft.to.join(', ') || '(ללא נמען)'}
                            </p>
                            {draft.bodyText && (
                              <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                                {draft.bodyText.replace(/<[^>]*>/g, '').substring(0, 100)}
                              </p>
                            )}
                            <p className="text-xs text-slate-400 mt-1">
                              עודכן: {new Date(draft.updatedAt).toLocaleDateString('he-IL', {
                                day: 'numeric',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </p>
                          </div>
                          <FiChevronRight className="text-slate-400 flex-shrink-0 text-base md:text-lg mt-0.5" />
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex-1 flex flex-col overflow-hidden">
              {/* Filter Controls for Inbox */}
              {view === 'inbox' && threads.length > 0 && (
                <div className="border-b border-slate-200 p-2 md:p-3 bg-slate-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs md:text-sm text-slate-600 whitespace-nowrap">סינון:</span>
                    <div className="flex items-center gap-1 flex-wrap">
                      <button
                        onClick={() => setReadFilter('all')}
                        className={`px-2 md:px-3 py-1.5 md:py-1 text-xs md:text-sm rounded-lg transition-colors touch-manipulation ${
                          readFilter === 'all'
                            ? 'bg-blue-600 text-white'
                            : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-100'
                        }`}
                      >
                        הכל
                      </button>
                      <button
                        onClick={() => setReadFilter('unread')}
                        className={`px-2 md:px-3 py-1.5 md:py-1 text-xs md:text-sm rounded-lg transition-colors flex items-center gap-1 touch-manipulation ${
                          readFilter === 'unread'
                            ? 'bg-blue-600 text-white'
                            : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-100'
                        }`}
                      >
                        לא נקרא
                        {threads.filter(t => t.unreadCount > 0).length > 0 && (
                          <span className="bg-blue-500 text-white text-[10px] md:text-xs px-1 md:px-1.5 py-0.5 rounded-full leading-tight min-w-[16px] md:min-w-[18px] text-center">
                            {threads.filter(t => t.unreadCount > 0).length}
                          </span>
                        )}
                      </button>
                      <button
                        onClick={() => setReadFilter('read')}
                        className={`px-2 md:px-3 py-1.5 md:py-1 text-xs md:text-sm rounded-lg transition-colors touch-manipulation ${
                          readFilter === 'read'
                            ? 'bg-blue-600 text-white'
                            : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-100'
                        }`}
                      >
                        נקרא
                      </button>
                    </div>
                  </div>
                  <span className="text-xs text-slate-500 whitespace-nowrap">
                    מציג {filteredThreads.length} מתוך {threads.length}
                  </span>
                </div>
              )}

              <div ref={scrollContainerRef} className="flex-1 overflow-y-auto p-4">
                {loading && threads.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <div className="relative mb-4">
                        <FiMail className="text-5xl mx-auto text-blue-600 animate-pulse" />
                        <FiLoader className="absolute -top-1 -right-1 text-2xl text-blue-400 animate-spin" />
                      </div>
                      <div className="flex items-center justify-center gap-1 text-slate-600 font-medium">
                        <span className="animate-bounce" style={{ animationDelay: '0ms' }}>מביא</span>
                        <span className="animate-bounce" style={{ animationDelay: '150ms' }}>מיילים</span>
                        <span className="animate-bounce" style={{ animationDelay: '300ms' }}>חדשים</span>
                        <span className="flex gap-0.5">
                          <span className="animate-bounce" style={{ animationDelay: '450ms' }}>.</span>
                          <span className="animate-bounce" style={{ animationDelay: '600ms' }}>.</span>
                          <span className="animate-bounce" style={{ animationDelay: '750ms' }}>.</span>
                        </span>
                      </div>
                    </div>
                  </div>
                ) : !loading && threads.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center text-slate-500">
                      <FiMail className="text-4xl mx-auto mb-2 opacity-50" />
                      <p>אין הודעות בתיבת הדואר</p>
                      <p className="text-xs mt-2 text-slate-400">
                        {isConnected ? 'התיבה ריקה' : 'יש להתחבר ל-Gmail כדי לראות מיילים'}
                      </p>
                    </div>
                  </div>
                ) : filteredThreads.length === 0 && threads.length > 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center text-slate-500">
                      <FiMail className="text-4xl mx-auto mb-2 opacity-50" />
                      <p>
                        {readFilter === 'unread' ? 'אין הודעות לא נקראות' :
                         readFilter === 'read' ? 'אין הודעות נקראות' :
                         'אין הודעות'}
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      {filteredThreads.map((thread, index) => (
                        <motion.div
                          key={`filtered-thread-${thread.id || index}-${index}`}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          onClick={() => handleThreadClick(thread.id)}
                          className={`
                            p-3 md:p-4 rounded-lg border border-slate-200
                            hover:bg-slate-50 active:bg-slate-100 cursor-pointer
                            transition-colors touch-manipulation
                            ${thread.unreadCount > 0 ? 'bg-blue-50 border-blue-200' : ''}
                          `}
                        >
                          <div className="flex items-start justify-between gap-2 md:gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1 md:gap-2 mb-1 flex-wrap">
                                <span className="font-semibold text-slate-900 truncate text-sm md:text-base">
                                  {thread.subject || '(ללא נושא)'}
                                </span>
                                {thread.unreadCount > 0 && (
                                  <>
                                    <span className="bg-green-500 text-white text-[10px] md:text-xs px-1 md:px-1.5 lg:px-2 py-0.5 rounded-full font-medium flex-shrink-0 leading-tight">
                                      חדש
                                    </span>
                                    <span className="bg-blue-500 text-white text-[10px] md:text-xs px-1 md:px-1.5 lg:px-2 py-0.5 rounded-full flex-shrink-0 leading-tight min-w-[16px] md:min-w-[18px] text-center">
                                      {thread.unreadCount}
                                    </span>
                                  </>
                                )}
                              </div>
                              <p className="text-xs md:text-sm text-slate-600 truncate">
                                {thread.from}
                              </p>
                              <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                                {thread.snippet}
                              </p>
                              <p className="text-xs text-slate-400 mt-1">
                                {new Date(thread.lastMessageDate).toLocaleDateString('he-IL', {
                                  day: 'numeric',
                                  month: 'short',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </p>
                            </div>
                            <FiChevronRight className="text-slate-400 flex-shrink-0 text-base md:text-lg mt-0.5" />
                          </div>
                        </motion.div>
                      ))}
                    </div>
                    
                    {/* Loading indicator at bottom when loading more */}
                    {loading && threads.length > 0 && (
                      <div className="flex items-center justify-center py-4 mt-2">
                        <FiLoader className="text-2xl text-blue-600 animate-spin mr-2" />
                        <span className="text-sm text-slate-500">טוען עוד מיילים...</span>
                      </div>
                    )}
                  </>
                )}
              </div>
              
              {/* Pagination Controls */}
              {view === 'inbox' && threads.length > 0 && (
                <div className="border-t border-slate-200 p-2 md:p-3 bg-slate-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs md:text-sm text-slate-600 whitespace-nowrap">מספר פריטים לדף:</span>
                    <select
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        goToFirstPage();
                      }}
                      className="px-2 py-1.5 md:py-1 border border-slate-300 rounded-lg text-xs md:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 touch-manipulation"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-1 md:gap-2 flex-wrap justify-center sm:justify-end">
                    <button
                      onClick={goToFirstPage}
                      disabled={pageTokens.length === 0}
                      className="px-2 md:px-3 py-1.5 md:py-1 text-xs md:text-sm border border-slate-300 rounded-lg hover:bg-slate-100 active:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 touch-manipulation"
                    >
                      <FiChevronsRight className="text-sm md:text-base" />
                      <span className="hidden sm:inline">ראשון</span>
                    </button>
                    <button
                      onClick={goToPrevPage}
                      disabled={pageTokens.length === 0}
                      className="px-2 md:px-3 py-1.5 md:py-1 text-xs md:text-sm border border-slate-300 rounded-lg hover:bg-slate-100 active:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 touch-manipulation"
                    >
                      <FiChevronRight className="text-sm md:text-base" />
                      <span className="hidden sm:inline">הקודם</span>
                    </button>
                    <span className="text-xs md:text-sm text-slate-600 px-2 md:px-3 whitespace-nowrap">
                      עמוד {pageTokens.length + 1}
                    </span>
                    <button
                      onClick={goToNextPage}
                      disabled={!hasMore || !nextPageToken}
                      className="px-2 md:px-3 py-1.5 md:py-1 text-xs md:text-sm border border-slate-300 rounded-lg hover:bg-slate-100 active:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 touch-manipulation"
                    >
                      <span className="hidden sm:inline">הבא</span>
                      <FiChevronLeft className="text-sm md:text-base" />
                    </button>
                    {hasMore && (
                      <span className="text-xs text-slate-500 whitespace-nowrap">
                        ({threads.length} פריטים)
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
            )}
          </div>
          </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Compose Email Modal - Outside AnimatePresence to avoid conflicts */}
      <EmailCompose
        isOpen={showCompose}
        onClose={handleComposeClose}
        initialData={composeInitialData}
        onSent={() => {
          loadInbox();
          loadSent();
          loadDrafts();
          // Switch to sent view after sending
          setView('sent');
        }}
      />

      {/* Disconnect Confirmation Modal */}
      {showDisconnectConfirm && (
        <div className="fixed inset-0 z-[200] bg-black/50 flex items-center justify-center p-4" onClick={() => !disconnecting && setShowDisconnectConfirm(false)}>
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-lg mb-4">נתק Gmail</h3>
            <p className="text-sm text-gray-700 mb-6">
              האם אתה בטוח שברצונך לנתק את חשבון Gmail שלך? כל הטוקנים יימחקו ותצטרך להתחבר מחדש.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDisconnectConfirm(false)}
                disabled={disconnecting}
                className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium disabled:opacity-50"
              >
                ביטול
              </button>
              <button
                onClick={handleDisconnect}
                disabled={disconnecting}
                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-semibold disabled:opacity-50 flex items-center gap-2"
              >
                {disconnecting ? (
                  <>
                    <FiLoader className="animate-spin" />
                    מנתק...
                  </>
                ) : (
                  <>
                    <FiLogOut />
                    נתק
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


